import { HttpTransportType, HubConnection, HubConnectionBuilder, IRetryPolicy, LogLevel, RetryContext } from "@microsoft/signalr";
import { appConst } from "../AppConst";
import { NotifyHelper } from "../helpers/toast";
import { setQueueToken, clearQueueToken } from "../helpers/queueToken";
import { getActivePeriodId, setActivePeriodId, markPeriodResolved, getSessionId } from "../api/apiClient";
import { registrationApi } from "../api/registration/registrationApi";
import { IAccessGrantedPayload } from "../model/respone/queue/IAccessGrantedPayload";
import { IQueuePositionPayload } from "../model/respone/queue/IQueuePositionPayload";
import { IQueueTickPayload } from "../model/respone/queue/IQueueTickPayload";
import {
    IRegistrationConfirmedPayload,
    IRegistrationFailedPayload,
} from "../model/respone/registration/IRegistrationResult";
import { rootActions } from "../state/actions/rootActions";
import { store } from "../state/store";

/**
 * Reconnect policy chống thundering herd 50k SV cùng đứt khi WebApi restart / network blip.
 *
 * Default `withAutomaticReconnect([0, 2000, 5000, 10000])` có 2 vấn đề:
 *   1. KHÔNG có jitter → 50k client reconnect tại đúng các mốc 0/2/5/10s → spike 200k Redis ops
 *      cho `Hub.OnConnectedAsync` (4 ops/connect × 50k).
 *   2. Sau 17s cạn retry → onclose(err) → hard-logout → 50k POST /api/auth/login storm tier-2.
 *
 * Policy này:
 *   - Backoff exponential capped: 1s → 2s → 4s → 8s → 16s → 30s → 60s (giữ 60s mãi).
 *   - Jitter ±50% trên mỗi delay → 50k client trải đều thay vì pulse cùng lúc.
 *   - KHÔNG bao giờ trả null → SignalR không bao giờ vào trạng thái Disconnected do cạn retry
 *     → onclose(err) chỉ fire khi server thực sự kill connection (auth invalid).
 *     SV mất mạng N giờ vẫn tự reconnect khi mạng phục hồi, không bị đẩy ra /login.
 */
class JitteredRetryPolicy implements IRetryPolicy {
    private static readonly BASE_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000, 60000];

    nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
        const idx = Math.min(retryContext.previousRetryCount, JitteredRetryPolicy.BASE_DELAYS_MS.length - 1);
        const base = JitteredRetryPolicy.BASE_DELAYS_MS[idx];
        // Jitter ±50% — full-jitter style. 50k client cùng disconnect → trải đều trong [base/2, base*1.5].
        const jitter = base * (Math.random() - 0.5);
        return Math.max(500, Math.floor(base + jitter));
    }
}

/**
 * Singleton wrapper SignalR connection cho RegistrationHub.
 * Backend Hub events: QueuePosition, QueueTick, AccessGranted, RegistrationConfirmed,
 *                    RegistrationFailed, PeriodClosed.
 *
 * Lifecycle:
 *   start(jwt) — gọi sau LOGIN_SUCCESS hoặc RESTORE_SESSION (HubProvider quản lý).
 *   stop()    — gọi khi LOGOUT hoặc PeriodClosed.
 */
class RegistrationHubClient {
    private connection: HubConnection | null = null;
    private starting = false;
    /** True khi caller chủ động stop (logout). Phân biệt với onclose do JWT hết hạn. */
    private intentionalStop = false;

    async start(jwt: string): Promise<void> {
        if (this.starting) return;

        // Re-auth race fix: nếu connection cũ vẫn còn (stop() trước đó chưa hoàn tất async),
        // PHẢI await stop xong trước khi build connection mới. Nếu không, check `if (connection)`
        // ở đầu return early → start() bị skip → SV mất realtime sau re-login.
        if (this.connection) {
            await this.stop();
        }

        this.starting = true;
        this.intentionalStop = false;

        try {
            // Backend map hub ở `/hubs/registration` (KHÔNG có `/api` prefix).
            const url = `${appConst.apiOrigin}/hubs/registration`;
            this.connection = new HubConnectionBuilder()
                .withUrl(url, {
                    accessTokenFactory: () => jwt,
                    // WebSocket only + skip /negotiate → LB không cần sticky session.
                    // Mỗi connection = 1 WebSocket TCP bám 1 server suốt session;
                    // cross-server messaging do Redis Backplane lo.
                    transport: HttpTransportType.WebSockets,
                    skipNegotiation: true,
                })
                .withAutomaticReconnect(new JitteredRetryPolicy())
                .configureLogging(LogLevel.Warning)
                .build();

            this.attachHandlers(this.connection);

            // onreconnecting: server vừa rớt, retry policy đang chờ delay → show toast nhẹ
            // để SV biết hệ thống đang tự xử lý, KHÔNG cần F5.
            this.connection.onreconnecting(() => {
                NotifyHelper.Info("Mất kết nối tạm thời, đang thử kết nối lại...");
            });
            this.connection.onreconnected(() => {
                NotifyHelper.Success("Đã kết nối lại.");
            });

            // onclose chỉ fire khi: (a) caller chủ động stop, hoặc (b) JitteredRetryPolicy
            // bị disable vì error không retry được (auth fail server-side).
            // JitteredRetryPolicy KHÔNG BAO GIỜ trả null → onclose(err) ở đây = auth thực sự fail.
            this.connection.onclose((err) => {
                if (this.intentionalStop) return;
                if (err) {
                    // eslint-disable-next-line no-console
                    console.warn("[Hub] disconnected unexpectedly:", err);
                    this.handleAuthFailure();
                }
            });

            await this.connection.start();
            // eslint-disable-next-line no-console
            console.log("[Hub] Connected /hubs/registration");
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[Hub] start failed:", err);
            this.connection = null;
            // CHỈ logout khi server thực sự trả 401 (JWT invalid). Mọi lỗi khác — WS bị load balancer
            // chặn (VMware LB chưa forward Upgrade), network chập, Backplane down — KHÔNG đá SV ra /login
            // (login qua REST vẫn hợp lệ). Chỉ mất realtime tạm; retry ngầm sau 5s, tự khỏi khi LB thông WS.
            const errAny = err as any;
            const is401 = errAny?.statusCode === 401
                || /401|unauthorized/i.test(errAny?.message ?? "");
            if (is401) {
                this.handleAuthFailure();
            } else if (!this.intentionalStop) {
                // Retry ngầm, KHÔNG toast mỗi lần để tránh spam khi WS bị chặn kéo dài.
                setTimeout(() => this.start(jwt), 5000);
            }
        } finally {
            this.starting = false;
        }
    }

    async stop(): Promise<void> {
        if (!this.connection) return;
        this.intentionalStop = true;
        try {
            await this.connection.stop();
        } catch {
            /* noop */
        } finally {
            this.connection = null;
        }
    }

    /** Mở rộng auth fail → toast + force logout + redirect /login. */
    private handleAuthFailure(): void {
        // Tránh double trigger — clear queue token và auth.
        clearQueueToken();
        NotifyHelper.Error("Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.");
        store.dispatch(rootActions.auth.LOGOUT());
        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.href = "/login";
        }
    }

    private attachHandlers(conn: HubConnection): void {
        // Multi-period: Hub đẩy event cho MỌI đợt SV thuộc. Chỉ áp dụng vào UI nếu event thuộc
        // đợt ĐANG CHỌN (X-Period-Id). Token thì lưu per-đợt bất kể đợt nào.
        conn.on("QueuePosition", (p: IQueuePositionPayload) => {
            if (p.token) setQueueToken(p.token, p.periodId);
            // Lần đầu (chưa chọn đợt) → adopt đợt đầu tiên Hub đẩy về làm đợt mặc định
            // để LandingPage /queue/status có X-Period-Id. SV đổi sau qua combobox ở /classes.
            if (p.periodId && !getActivePeriodId()) setActivePeriodId(p.periodId);
            if (p.periodId && getActivePeriodId() && p.periodId !== getActivePeriodId()) return;
            store.dispatch(rootActions.queue.SET_POSITION(p));
        });

        conn.on("QueueTick", (p: IQueueTickPayload) => {
            store.dispatch(rootActions.queue.TICK(p));
        });

        conn.on("AccessGranted", (p: IAccessGrantedPayload) => {
            setQueueToken(p.token, p.periodId);
            if (p.periodId && getActivePeriodId() && p.periodId !== getActivePeriodId()) return;
            store.dispatch(rootActions.queue.ACCESS_GRANTED(p));
        });

        conn.on("RegistrationConfirmed", (p: IRegistrationConfirmedPayload) => {
            store.dispatch(rootActions.registration.REGISTER_CONFIRMED(p));
        });

        conn.on("RegistrationFailed", (p: IRegistrationFailedPayload) => {
            store.dispatch(rootActions.registration.REGISTER_FAILED(p));
        });

        // Reaper tự huỷ kết quả do SV không "Lưu dữ liệu" quá hạn → toast + reload danh sách + slot.
        conn.on("RegistrationExpired", (p?: { periodId?: number; classIds?: number[]; message?: string }) => {
            NotifyHelper.Error(p?.message || "Kết quả đăng ký đã bị huỷ do bạn không bấm \"Lưu dữ liệu\" trong thời gian quy định.");
            store.dispatch(rootActions.registration.LOAD_MY_START());
            store.dispatch(rootActions.classes.LOAD_START());
            store.dispatch(rootActions.financial.LOAD_START());
        });

        // Single-device login: SV vừa đăng nhập ở thiết bị khác → đá thiết bị này ngay.
        // Bỏ qua nếu sid trong payload trùng sid đang giữ (chính phiên mình vừa login) → không tự đá.
        conn.on("ForceLogout", (p?: { sessionId?: string }) => {
            if (p?.sessionId && p.sessionId === getSessionId()) return;
            clearQueueToken();
            NotifyHelper.Error("Tài khoản của bạn vừa được đăng nhập ở thiết bị khác. Bạn đã bị đăng xuất tại đây.");
            store.dispatch(rootActions.auth.LOGOUT());
            if (typeof window !== "undefined" && window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        });

        conn.on("PeriodClosed", (p?: { periodId?: number }) => {
            const pid = p?.periodId;
            clearQueueToken(pid);
            // Đợt KHÁC đóng (không phải đợt đang xem) → bỏ qua, SV ở đợt đang chọn không bị ảnh hưởng.
            if (pid && getActivePeriodId() && pid !== getActivePeriodId()) return;

            // Đợt ĐANG XEM đóng → multi-period: nếu SV còn đợt mở khác thì CHUYỂN sang đó (không kick),
            // chỉ vào /closed khi KHÔNG còn đợt mở nào. Tránh báo "đã đóng cổng" sai khi đợt khác còn mở.
            (async () => {
                try {
                    const res = await registrationApi.GetPeriods();
                    const remaining = res.is_success && res.data
                        ? res.data.filter(x => x.periodId !== pid)
                        : [];
                    if (remaining.length > 0) {
                        const next = remaining[0];
                        setActivePeriodId(next.periodId);
                        store.dispatch(rootActions.queue.RESET());
                        // Reset list lớp + đăng ký đợt vừa đóng → không rò sang đợt mới (pending/confirmed
                        // theo classId, bảng "Đã đăng ký", danh sách lớp). LOAD_* ngay sau nạp lại đợt mới.
                        store.dispatch(rootActions.classes.RESET());
                        store.dispatch(rootActions.registration.RESET());
                        store.dispatch(rootActions.queue.LOAD_STATUS_START());
                        store.dispatch(rootActions.registration.LOAD_MY_START());
                        store.dispatch(rootActions.financial.LOAD_START());
                        store.dispatch(rootActions.classes.LOAD_START());
                        NotifyHelper.Info(`Đợt đăng ký vừa đóng. Đã chuyển sang đợt "${next.label}".`);
                        return;
                    }
                } catch { /* lỗi mạng → coi như hết đợt, kick /closed */ }
                // Không còn đợt mở nào → đóng cổng thật sự.
                setActivePeriodId(null);
                store.dispatch(rootActions.queue.PERIOD_CLOSED());
            })();
        });

        // Admin Mở cổng → reset state + ép LandingPage re-fetch /queue/status.
        // Saga LOAD_STATUS_START sẽ gọi GetStatus → Hub.OnConnectedAsync flow trên server đẩy
        // QueuePosition mới; SV đang đứng ở "Cổng đăng ký chưa mở" tự chuyển sang hàng chờ
        // mà không cần F5.
        // Cũng dispatch LOAD_MY_START: Header.tsx auto-load myList khi user authenticated;
        // nếu lúc đó cổng còn đóng → GetMyAsync trả empty (cache reg:period:{periodId}:meta = null)
        // → myStatus mắc kẹt is_completed với myList rỗng → ClassesPage mount sau không dispatch
        // lại (vì status !== is_not_initialization) → bảng "Đã đăng ký" trống cho đến khi F5.
        //
        // Jitter 0-3s chống thundering herd: 50k SV đồng loạt nhận broadcast → nếu cùng
        // dispatch 2 REST/SV trong 1ms = 100k req spike → cạn SQL pool + Redis ops.
        // Trải đều trong 3s window → peak ~13k/s tổng = ~1.3k/s/instance (với 10 instance LB).
        // Hạ từ 10s xuống 3s để công bằng hơn (biên độ ngẫu nhiên vị trí hàng đợi còn ~3s):
        // đánh đổi lấy đỉnh cao gấp ~3.3× — cân với capacity trước khi hạ tiếp.
        //
        // Delay TOÀN BỘ chain (kể cả PERIOD_OPENED) — nếu dispatch PERIOD_OPENED ngay,
        // queueReducer reset status=is_not_initialization → LandingPage useEffect tự re-fire
        // LOAD_STATUS_START bỏ qua jitter. Trade-off: SV thấy "Cổng đăng ký chưa mở" tối đa
        // thêm 3s sau khi admin bấm — chấp nhận được vì burst protection ưu tiên hơn.
        conn.on("PeriodOpened", () => {
            const jitterMs = Math.random() * 3000;
            setTimeout(async () => {
                // Đợt mới mở → re-resolve danh sách đợt SV thuộc (SV có thể vừa có đợt). Set đợt nếu
                // chưa chọn → các gate (Header/Landing) thoát "chưa mở cổng" + gắn X-Period-Id đúng.
                try {
                    const res = await registrationApi.GetPeriods();
                    const data = res.is_success && res.data ? res.data : [];
                    if (data.length > 0) {
                        const ids = data.map(p => p.periodId);
                        let pid = getActivePeriodId() ?? 0;
                        if (!ids.includes(pid)) pid = ids[0];
                        if (pid > 0) setActivePeriodId(pid);
                    }
                    markPeriodResolved();
                } catch { /* ignore */ }
                store.dispatch(rootActions.queue.PERIOD_OPENED());
                if (getActivePeriodId()) {
                    store.dispatch(rootActions.queue.LOAD_STATUS_START());
                    store.dispatch(rootActions.registration.LOAD_MY_START());
                }
            }, jitterMs);
        });
    }
}

export const registrationHub = new RegistrationHubClient();
